import { LANGUAGE_CHOICE_KEY, LANGUAGE_OFFER_DISMISSED_KEY } from 'BomdisoftComponents/language-offer/keys';
import { type SupportedLanguageCode, SupportedLanguageCodeEnum } from '@bomdisoft/translations';

export type LanguageOfferView = {
  readonly localStorage: Pick<Storage, 'getItem' | 'setItem'>;
  readonly location: Pick<Location, 'pathname'>;
  readonly navigator: Pick<Navigator, 'language' | 'languages'>;
};

const SUPPORTED = Object.keys(SupportedLanguageCodeEnum) as SupportedLanguageCode[];

const read = (view: LanguageOfferView, key: string): string | undefined => {
  try {
    return view.localStorage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
};

const write = (view: LanguageOfferView, key: string, value: string): boolean => {
  try {
    view.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

/**
 * The locale to offer, or undefined when there is nothing worth offering.
 *
 * Walks the browser's ordered preferences and stops at the first entry that is
 * either English or a locale this site publishes. Stopping at English matters:
 * a visitor whose first preference is English and second is French is reading
 * their first choice already, and offering French would be a downgrade.
 */
export const offeredLocale = (preferences: readonly string[], locales: readonly SupportedLanguageCode[] = SUPPORTED): SupportedLanguageCode | undefined => {
  for (const tag of preferences) {
    const code = tag.split('-')[0]?.toLowerCase();
    if (!code) continue;
    if (code === 'en') return undefined;
    if (locales.includes(code as SupportedLanguageCode)) return code as SupportedLanguageCode;
  }

  return undefined;
};

export const decideLanguageOffer = (view: LanguageOfferView): SupportedLanguageCode | undefined => {
  if (view.location.pathname !== '/') return undefined;
  if (read(view, LANGUAGE_OFFER_DISMISSED_KEY) === 'true') return undefined;
  if (read(view, LANGUAGE_CHOICE_KEY)) return undefined;

  return offeredLocale([...(view.navigator.languages ?? []), view.navigator.language].filter(Boolean));
};

export const recordLanguageChoice = (view: LanguageOfferView, offered: SupportedLanguageCode) => write(view, LANGUAGE_CHOICE_KEY, offered);

export const recordLanguageOfferDismissed = (view: LanguageOfferView) => write(view, LANGUAGE_OFFER_DISMISSED_KEY, 'true');
